export * from './layers';
import { LAYER_GROUPS, layerById, type GeojsonLayerDef, type MapLayerDef, type LegendItem, PD_TILE_URL } from './layers';

/** Designation datasets whose map layer has a different id. */
export const CHIP_LAYERS: Record<string, string[]> = {
  'agricultural-land-classification': ['alc-provisional'],
  'agricultural-land-classification-detailed': ['alc-detailed'],
  'crow-access-land': ['crow-access'],
  'registered-common-land': ['common-land'],
  'coal-development-high-risk-area': ['coal-high-risk'],
  'flood-risk-zone': ['flood-zone-3', 'flood-zone-2'],
};

/** Chip wording where it differs from the label the lookup service returns. */
export const CHIP_TEXT: Record<string, string> = {
  'agricultural-land-classification': 'Agricultural land classification (provisional)',
  'agricultural-land-classification-detailed': 'Agricultural land classification (detailed)',
};

/** Short chip wording so a row of chips fits without wrapping; the full wording stays in the chip's hover text and the detail panel. */
export const CHIP_SHORT: Record<string, string> = {
  'article-4-direction-area': 'Article 4',
  'air-quality-management-area': 'Air quality area',
  'nutrient-neutrality-catchment': 'Nutrient neutrality',
  'park-and-garden': 'Park or garden',
  'archaeological-priority-area': 'Archaeology area',
  'brownfield-land': 'Brownfield land',
  'listed-building-outline': 'Listed buildings',
  'scheduled-monument': 'Scheduled monuments',
  'locally-listed-building': 'Locally listed',
  'world-heritage-site-buffer-zone': 'World Heritage buffer',
  'world-heritage-site': 'World Heritage Site',
  'heritage-at-risk': 'Heritage at Risk',
  'flood-storage-area': 'Flood storage',
  'surface-water-flood-medium': 'Surface water 1%+',
  'surface-water-flood-low': 'Surface water 0.1%',
  'asset-of-community-value': 'Community asset',
  'priority-habitat-inventory': 'Priority habitat',
  'long-established-woodland': 'Long-est. woodland',
  'tree-preservation-zone': 'Tree preservation',
  'site-of-special-scientific-interest': 'SSSI',
  'special-area-of-conservation': 'SAC',
  'special-protection-area': 'SPA',
  'ramsar': 'Ramsar',
  'national-nature-reserve': 'National reserve',
  'local-nature-reserve': 'Local reserve',
  'area-of-outstanding-natural-beauty': 'AONB',
  'flood-risk-zone': 'Flood Zone 2 or 3',
  'flood-zone-3': 'Flood Zone 3',
  'flood-zone-2': 'Flood Zone 2',
  'agricultural-land-classification': 'Agricultural (provisional)',
  'agricultural-land-classification-detailed': 'Agricultural (surveyed)',
  'crow-access-land': 'Open access land',
  'coal-development-high-risk-area': 'Coal mining high risk',
  'mine-entries': 'Mine entries',
};

/** Text for a designation chip: the short wording where one exists, else the full wording. */
export function chipLabel(dataset: string, label: string): string {
  return CHIP_SHORT[dataset] ?? CHIP_TEXT[dataset] ?? (label || dataset);
}

const SMALL_WORDS = new Set(['of', 'the', 'and', 'at', 'in', 'on', 'to', 'for', 'by']);

/** Register names such as "CHURCH OF ST MARY" read better as "Church of St Mary"; a name already in mixed case is left alone. */
export function titleCaseName(name: string): string {
  const s = name.trim();
  if (!s || s !== s.toUpperCase()) return s;
  return s.toLowerCase()
    .replace(/(^|[\s(/-])(\p{L})(\p{L}*)/gu, (_m, pre: string, first: string, rest: string, offset: number) => {
      const word = first + rest;
      return pre + (offset > 0 && SMALL_WORDS.has(word) ? word : first.toUpperCase() + rest);
    })
    .replace(/(\d)([a-z])\b/g, (_m, d: string, l: string) => d + l.toUpperCase());
}

/** CSS background for a chip's dot: the layer's colour, or hard-edged slices of every colour in its legend (up to eight) so a multi-colour layer is not mistaken for one of them. */
export function swatchBackground(legend: readonly LegendItem[]): string | undefined {
  const colours = [...new Set(legend.map(i => i.colour))].slice(0, 8);
  if (colours.length === 0) return undefined;
  if (colours.length === 1) return colours[0];
  const step = 100 / colours.length;
  return `conic-gradient(${colours.map((c, i) => `${c} ${(i * step).toFixed(2)}% ${((i + 1) * step).toFixed(2)}%`).join(', ')})`;
}

/** Mine entries are tile-only (no lookup), so they are shown whenever the high risk area they sit within is present. */
export const COAL_DATASET = 'coal-development-high-risk-area';
export const MINE_ENTRIES_NOTE = 'Shafts and adits cannot be counted here; switch this on to look for them.';

export interface ConstraintLike {
  dataset: string;
  label: string;
  status: string;
  count: number;
  names: string[];
}

/** The map layers that draw a dataset (none for a dataset with no map layer). */
export function layerIdsFor(dataset: string): string[] {
  return (CHIP_LAYERS[dataset] ?? [dataset]).filter(id => layerById(id));
}

export interface PlannedFeature { title: string; count: number; names: string[] }

export interface PlannedMap {
  group: string;
  layers: MapLayerDef[];
  features: PlannedFeature[];
}

/** One map per designation group that has something Present, in the order the groups are listed on screen. */
export function planSiteMaps(constraints: readonly ConstraintLike[]): PlannedMap[] {
  const byGroup = new Map<string, PlannedMap>();
  const add = (group: string): PlannedMap => {
    let p = byGroup.get(group);
    if (!p) { p = { group, layers: [], features: [] }; byGroup.set(group, p); }
    return p;
  };
  for (const c of constraints) {
    if (c.status !== 'Present') continue;
    const ids = layerIdsFor(c.dataset);
    if (c.dataset === COAL_DATASET) ids.push('mine-entries');
    for (const id of ids) {
      const def = layerById(id)!;
      const plan = add(def.group);
      if (!plan.layers.some(l => l.id === id)) plan.layers.push(def);
      if (id !== 'mine-entries' && !plan.features.some(f => f.title === (CHIP_TEXT[c.dataset] ?? (c.label || c.dataset)))) {
        plan.features.push({ title: CHIP_TEXT[c.dataset] ?? (c.label || c.dataset), count: c.count, names: c.names });
      }
    }
  }
  return LAYER_GROUPS.filter(g => byGroup.has(g)).map(g => byGroup.get(g)!);
}

/** A closed ring approximating a circle of `radiusM` metres around the point, as [lon, lat] pairs. */
export function circleRing(lat: number, lon: number, radiusM: number, steps = 64): [number, number][] {
  const dLat = radiusM / 111_320;
  const dLon = radiusM / (111_320 * Math.cos((lat * Math.PI) / 180));
  const ring: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * 2 * Math.PI;
    ring.push([lon + dLon * Math.cos(a), lat + dLat * Math.sin(a)]);
  }
  return ring;
}

/** The part of the map the Locate screen frames around the pin: west, south, east, north. */
export function frameBounds(lat: number, lon: number, radiusM: number): [number, number, number, number] {
  const r = Math.max(radiusM, 400);
  const dLat = r / 111_320;
  const dLon = r / (111_320 * Math.cos((lat * Math.PI) / 180));
  return [lon - dLon, lat - dLat, lon + dLon, lat + dLat];
}

export const OVERLAY_FILL_OPACITY = { vector: 0.28, raster: 0.7, geojson: 0.5 } as const;

const srcId = (id: string) => `ov-${id}`;

/** Colour expression for a layer coloured by an attribute, or the plain colour. */
export function fillColourFor(def: GeojsonLayerDef): unknown {
  if (!def.colourField || !def.gradeColours) return def.colour;
  const pairs = Object.entries(def.gradeColours).flatMap(([k, v]) => [k, v]);
  return ['match', ['get', def.colourField], ...pairs, '#bdbdbd'];
}

export interface OverlayStyle {
  sources: Record<string, unknown>;
  layers: Record<string, unknown>[];
}

/** The style sources and layers that draw one overlay; a GeoJSON overlay is given its features up front. */
export function overlayStyle(def: MapLayerDef, data?: unknown): OverlayStyle {
  const id = srcId(def.id);
  if (def.kind === 'raster') {
    return {
      sources: { [id]: { type: 'raster', tiles: [def.tiles], tileSize: 256, maxzoom: def.maxZoom } },
      layers: [{ id: `${id}-fill`, type: 'raster', source: id, paint: { 'raster-opacity': OVERLAY_FILL_OPACITY.raster } }],
    };
  }
  if (def.kind === 'vector') {
    const source = { [id]: { type: 'vector', tiles: [PD_TILE_URL(def.dataset)], minzoom: 4, maxzoom: 14 } };
    if (def.shape === 'point') {
      return {
        sources: source,
        layers: [{ id: `${id}-fill`, type: 'circle', source: id, 'source-layer': def.dataset, paint: { 'circle-color': def.colour, 'circle-radius': 4, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1 } }],
      };
    }
    const only = def.only ? { filter: ['in', ['to-string', ['get', def.only.property]], ['literal', def.only.values]] } : {};
    return {
      sources: source,
      layers: [
        { id: `${id}-fill`, type: 'fill', source: id, 'source-layer': def.dataset, ...only, paint: { 'fill-color': def.colour, 'fill-opacity': OVERLAY_FILL_OPACITY.vector } },
        { id: `${id}-line`, type: 'line', source: id, 'source-layer': def.dataset, ...only, paint: { 'line-color': def.colour, 'line-width': 1.2 } },
      ],
    };
  }
  return {
    sources: { [id]: { type: 'geojson', data: data ?? { type: 'FeatureCollection', features: [] } } },
    layers: [
      { id: `${id}-fill`, type: 'fill', source: id, paint: { 'fill-color': fillColourFor(def), 'fill-opacity': OVERLAY_FILL_OPACITY.geojson } },
      { id: `${id}-line`, type: 'line', source: id, paint: { 'line-color': '#555555', 'line-width': 0.6, 'line-opacity': 0.6 } },
    ],
  };
}

/** The key entries to print for a layer: its whole legend, or for a graded layer only the grades actually drawn. */
export function keyFor(def: MapLayerDef, gradesDrawn?: ReadonlySet<string>): LegendItem[] {
  if (def.legend.length <= 1 || !gradesDrawn) return def.legend;
  return def.legend.filter(item => item.grade !== undefined && gradesDrawn.has(item.grade));
}
